import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

const VIEWPORT_MARGIN = 8;

const TRIGGER_GAP = 10;

interface IPopoverProps {
    content: ReactNode;
    maxWidth?: number;
    trigger?: 'hover' | 'click';
    block?: boolean;
    children: ReactNode;
}

const Popover = ({
    content,
    maxWidth = 400,
    trigger = 'hover',
    block = false,
    children,
}: IPopoverProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [anchor, setAnchor] = useState<DOMRect | null>(null);
    const [shift, setShift] = useState(0);

    const containerRef = useRef<HTMLSpanElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const close = () => {
        setIsOpen(false);
        setAnchor(null);
        setShift(0);
    };

    useLayoutEffect(() => {
        if (!isOpen) return;

        const update = () => {
            const rect = containerRef.current?.getBoundingClientRect();

            if (rect) setAnchor(rect);
        };

        update();

        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);

        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [isOpen]);

    useLayoutEffect(() => {
        const element = popoverRef.current;

        if (!isOpen || !anchor || !element) return;

        const rect = element.getBoundingClientRect();
        const left = rect.left - shift;
        const right = rect.right - shift;

        let next = 0;

        if (right > window.innerWidth - VIEWPORT_MARGIN) {
            next = window.innerWidth - VIEWPORT_MARGIN - right;
        }

        if (left + next < VIEWPORT_MARGIN) {
            next = VIEWPORT_MARGIN - left;
        }

        if (next !== shift) setShift(next);
    }, [isOpen, anchor, shift]);

    useEffect(() => {
        if (trigger !== 'click' || !isOpen) return;

        const onDocumentClick = (event: MouseEvent) => {
            const target = event.target as Node;

            if (containerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
                return;
            }

            setIsOpen(false);
            setAnchor(null);
            setShift(0);
        };

        document.addEventListener('click', onDocumentClick);

        return () => document.removeEventListener('click', onDocumentClick);
    }, [trigger, isOpen]);

    const triggerProps =
        trigger === 'click'
            ? { onClick: () => (isOpen ? close() : setIsOpen(true)) }
            : {
                  onMouseEnter: () => setIsOpen(true),
                  onMouseLeave: close,
                  onFocus: () => setIsOpen(true),
                  onBlur: close,
              };

    return (
        <span
            ref={containerRef}
            style={{
                position: 'relative',
                display: 'inline-block',
                width: block ? '100%' : undefined,
            }}
            {...triggerProps}
        >
            {children}

            {isOpen &&
                anchor &&
                createPortal(
                    <div
                        ref={popoverRef}
                        className="popover top"
                        role="tooltip"
                        style={{
                            display: 'block',
                            position: 'fixed',
                            top: anchor.top,
                            left: anchor.left + anchor.width / 2,
                            right: 'auto',
                            bottom: 'auto',
                            margin: 0,
                            transform:
                                'translate(-50%, -100%) translate(' +
                                shift +
                                'px, ' +
                                -TRIGGER_GAP +
                                'px)',
                            whiteSpace: 'normal',
                            maxWidth,
                            width: 'max-content',
                        }}
                    >
                        <div
                            className="arrow"
                            style={{ left: 'calc(50% - ' + shift + 'px)' }}
                        ></div>
                        <div className="popover-content" style={{ whiteSpace: 'pre-line' }}>
                            {content}
                        </div>
                    </div>,
                    document.body,
                )}
        </span>
    );
};

export default Popover;
